import React, { useEffect, useState, Suspense, lazy } from "react";
import { connect } from "react-redux";
import Route from "react-router-dom/Route";
import Switch from "react-router-dom/Switch";
import Container from "react-bootstrap/Container";
import Spinner from "react-bootstrap/Spinner";
import "./App.scss";
import routes from "./constants/routes";
import Home from "./containers/Home";
import Search from "./containers/Search";
import WidgetOnly from "./containers/WidgetOnly";
import NotFoundPage from "./containers/NotFoundPage";
import Header from "./components/Header";
import Footer from "./components/Footer";
import TechFooter from "./components/TechFooter";
import { setDirection } from "./redux/ui/action";
import { updatePrefsRequest } from "./redux/prefs/prefsSlice";
import { initializeApp } from "./redux/app/appSlice";
import { setAnonymousClientId } from "./redux/user/userSlice";
import { RTL_DIRECTION } from "./constants/default";
import { useTranslation } from "react-i18next";
import * as queryString from "query-string";
import {useLocation} from "react-router-dom";
import { identifyUser, clearIdentity } from './utils/analytics';
import useResidencyPreselection from "./hooks/useResidencyPreselection";
// import HelloScreen from "./components/HelloScreen";

// Lazy load the UnsubscribePage
const UnsubscribePage = lazy(() => import("./containers/UnsubscribePage"));
const SubscriptionsPage = lazy(() => import("./containers/SubscriptionsPage"));
const ProfilePage = lazy(() => import("./containers/ProfilePage"));

function App({ dispatch, dir, /*lng,*/ currency, lang, user, initialized }) {
  // console.log('creating app...')
  const { i18n } = useTranslation(); // eslint-disable-line
  const [cid, setCid] = useState(null);
  // console.log(`cid on App render: ${cid}`)

  const rtl = dir === "rtl";
  // if (lng === undefined) {
  //   const lng = i18n.language.substring(0, 2);
  // // }
  // dispatch(setDirection(RTL_DIRECTION[lng] ? "rtl" : "ltr"));
  // dispatch(setLanguage(lng));

  const currentLocation = useLocation();
  const params = queryString.parse(currentLocation.search);
  const { cur } = params;

  // Use residency preselection hook
  useResidencyPreselection();

  // if (currency === undefined) {
  //   // set currency based on device locale. default to IL.
  //   const country = navigator.languages.length > 0 && navigator.languages[0].indexOf('-') > -1 ? navigator.languages[0].split('-')[1] : 'IL'
  //   const countryToCurrency = require('country-to-currency');
  //   currency = countryToCurrency[country]
  //   console.log(`setting initial currency to ${currency} based on country ${country} ...`)
  //   dispatch(setCurrency(currency))
  // }

  useEffect(() => {
    // brought this from Search.js container to make it available also for homepage
    if (window.gtag) {
      window.gtag('get', 'G-SEK9X7V9L8', 'client_id', (client_id) => {
          console.log(`pulled gtag cid: ${client_id}`)
          setCid(client_id)
          // Set anonymous client ID in Redux (will also persist to localStorage)
          dispatch(setAnonymousClientId(client_id))
        })
    } else {
      // console.log('no gtag')
      // Generate anonymous client ID if GA is not available
      const existingId = localStorage.getItem('externalClientId')
      if (!existingId) {
        const generatedId = `ext_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        dispatch(setAnonymousClientId(generatedId))
      } else {
        dispatch(setAnonymousClientId(existingId))
      }
    }

    // Initialize app - this will load user profile if user is already logged in
    dispatch(initializeApp());
  }, [dispatch]);

  useEffect(() => {
    if (initialized && cur && currency !== cur) {
      console.log('setting currency from query string after initialization...')
      dispatch(updatePrefsRequest({ cur: cur }))
    }
  }, [initialized, cur, currency, dispatch]);

  // When user state becomes available (e.g., after initializeApp), identify user in GA
  useEffect(() => {
    try {
      if (user) {
        // user is the decoded token object stored in Redux
        if (user.id) {
          identifyUser(user.id);
        }
      } else {
        clearIdentity();
      }
    } catch (e) {
      // ignore
    }
  }, [user]);

  useEffect(() => {
    const lng = i18n.language;
    dispatch(setDirection(RTL_DIRECTION[lng] ? "rtl" : "ltr"));
    // Sync detected language to prefs if different
    if (lng !== lang) {
      dispatch(updatePrefsRequest({ lang: lng, langManuallySet: false }));
    }
  }, [i18n.language, lang, dispatch]);

  return (
    <Switch>
      <Route path={`${routes.LANGS}/${routes.WIDGET}`}>
        <WidgetOnly />
        <TechFooter enableLogRocket="false" user={user} />
      </Route>
      <Route>
        <div
          className={`main d-flex flex-column min-vh-100 ${
            rtl ? "text-right" : "text-left"
          }`}
          dir={rtl ? "rtl" : "ltr"}
        >
          <Header cid={cid} />
          <Suspense fallback={
            <Container fluid className="page-wrapper flex-fill d-flex align-items-center justify-content-center">
              <Spinner animation="border" role="status">
                <span className="sr-only">Loading...</span>
              </Spinner>
            </Container>
          }>
            <Switch>
              <Route path={`${routes.LANGS}/${routes.SEARCH}`} render={(props) => <Search {...props} cid={cid} />} />
              <Route path={`${routes.LANGS}/${routes.UNSUBSCRIBE}`} component={UnsubscribePage} />
              <Route path={`${routes.LANGS}/${routes.SUBSCRIPTIONS}`} component={SubscriptionsPage} />
              <Route path={`${routes.LANGS}/${routes.PROFILE}`} component={ProfilePage} />
              <Route exact path={`${routes.LANGS}`} render={(props) => <Home {...props} cid={cid} />} />
              <Route component={NotFoundPage} />
            </Switch>
          </Suspense>
          <Footer />
          <TechFooter enableLogRocket="true" user={user} />
          {/* <HelloScreen /> */}
        </div>
      </Route>
    </Switch>
  );
}

// export default App;

const mapStateToProps = ({ vendors, results, ui, currencies, prefs, user, app }) => ({
  dir: ui.direction,
  // lng: ui.language,
  currency: prefs.cur, // Use preferences store as source of truth
  lang: prefs.lang, // Add lang to props
  user: user.user, // Pass user to component for initialization logic
  initialized: app.initialized,
});

export default connect(mapStateToProps)(App);
